import { useQuery, UseQueryOptions } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { QueryFeature } from '../../../config/queryClient';

export interface TotalsDTO {
  costBasis: number | null;
  gainLoss: number | null;
  marketValue: number;
  percent: number;
}

interface PositionDTO {
  costBasis: number | null;
  gainLoss: number | null;
  marketValue: number;
  percent: number;
  price: number;
  units: number;
  name: string;
  ticker: string;
}

interface AssetDTO {
  id: string;
  color: string | null;
  costBasis: number | null;
  gainLoss: number | null;
  marketValue: number;
  name: string;
  percent: number;
  positions: PositionDTO[];
}

interface GroupDTO {
  name: string;
  rows: AssetDTO[];
  rowsTotal: TotalsDTO;
}

export interface PositionsDetailsDTO {
  groups: GroupDTO[];
  defaultGroup: string | null;
}

interface FetchPositionsDetailsParams {
  clientId: string;
  date: string;
  accountId: string;
  groupId: string;
}

const fetchPositionsDetails = async ({
  clientId,
  accountId,
  groupId,
  date,
}: FetchPositionsDetailsParams): Promise<PositionsDetailsDTO> => {
  return authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/PositionsDetails',
      {
        clientId,
        asOfDate: date,
        accountId: accountId || null,
        groupId: groupId || null,
      },
      { skipNulls: true },
    ),
  );
};

interface UsePositionsDetailsQueryParams {
  clientId: string;
  date: string;
  accountId: string;
  groupId: string;
}

export const POSITIONS_DETAILS_QUERY_KEY = 'positionsDetails';

export const usePositionsDetailsQuery = (
  { clientId, accountId, groupId, date }: UsePositionsDetailsQueryParams,
  options?: UseQueryOptions<PositionsDetailsDTO, Error>,
) => {
  return useQuery(
    [
      QueryFeature.AccountPerformanceReporting,
      POSITIONS_DETAILS_QUERY_KEY,
      clientId,
      date,
      accountId,
      groupId,
    ],

    () => fetchPositionsDetails({ clientId, date, accountId, groupId }),

    {
      retry: false,
      ...options,
    },
  );
};
